import classNames from "classnames/dedupe";
import React, { FC, useEffect, useState, useCallback } from "react";

import { NavArrowBackIcon, NavArrowNextIcon } from "../../../icons";
import { Button } from "../../ActionsSelectors/Button";
import PaginationItem from "./PaginationItem/PaginationItem";

export interface IPaginationProps {
  /**
   * Total number of pages
   */
  count: number;
  /**
   * Currently selected page
   */
  activePage?: number;
  /**
   * Number of page items to render between
   * navigation button and ellipsis
   */
  boundaryCount?: number;
  /**
   * Number of page items to render between
   * currently selected page and ellipsis
   */
  siblingCount?: number;
  /**
   * Set true if pagination is rendered on mobile display.
   * Navigation buttons become touch controlls, pagination rendered smaller
   */
  isMobile?: boolean;
  /**
   * Callback function on when currently active page has changed
   */
  onChange?: (newPageId: number) => void;
}

const Pagination: FC<IPaginationProps> = ({
  count,
  activePage = 1,
  siblingCount = 1,
  isMobile,
  boundaryCount = isMobile ? 0 : 1,
  onChange,
}) => {
  const [page, setPageState] = useState<number>(activePage);
  useEffect(() => {
    setPageState(activePage || 1);
  }, [activePage]);

  const range = (start, end) => {
    const length = end - start + 1;
    return Array.from({ length }, (_, i) => start + i);
  };

  const startPages = range(1, Math.min(boundaryCount, count));
  const endPages = range(
    Math.max(count - boundaryCount + 1, boundaryCount + 1),
    count,
  );

  const siblingsStart = Math.max(
    Math.min(page - siblingCount, count - boundaryCount - siblingCount * 2 - 1),
    boundaryCount + 2,
  );

  const siblingsEnd = Math.min(
    Math.max(page + siblingCount, boundaryCount + siblingCount * 2 + 2),
    endPages.length > 0 ? endPages[0] - 2 : count - 1,
  );

  const itemList = [
    ...startPages,

    // eslint-disable-next-line no-nested-ternary
    ...(siblingsStart > boundaryCount + 2
      ? ["ellipsis-plus-count"]
      : boundaryCount + 1 < count - boundaryCount
      ? [boundaryCount + 1]
      : []),

    ...range(siblingsStart, siblingsEnd),

    // eslint-disable-next-line no-nested-ternary
    ...(siblingsEnd < count - boundaryCount - 1
      ? ["ellipsis-minus-count"]
      : count - boundaryCount > boundaryCount
      ? [count - boundaryCount]
      : []),

    ...endPages,
  ];

  const handlePageChange = useCallback(
    (newPageId: number): void => {
      onChange && onChange(newPageId);
      setPageState(newPageId);
    },
    [onChange],
  );

  const handleNextPage = useCallback(() => {
    if (page < count) {
      const newPageId = page + 1;
      onChange && onChange(newPageId);
      setPageState(newPageId);
    }
  }, [count, onChange, page]);

  const handlePrevPage = useCallback(() => {
    if (page > 0) {
      const newPageId = page - 1;
      onChange && onChange(newPageId);
      setPageState(newPageId);
    }
  }, [onChange, page]);

  const showPrevNavigation = page === 1 ? "hidden" : "initial";
  const showNextNavigation = page === count ? "hidden" : "initial";

  const buttonClasses = classNames("btn-nav");

  const wrapperClasses = classNames("pagination-wrapper", {
    "pagination-pages-holder--mobile": isMobile,
  });

  return (
    <div className={wrapperClasses}>
      <Button
        data-testid="Pagination__prev"
        variant="icon"
        classes={{ root: buttonClasses }}
        onClick={handlePrevPage}
        style={{ visibility: showPrevNavigation }}
      >
        <NavArrowBackIcon />
      </Button>
      <ul className="pagination-pages-holder">
        {itemList.map((item) => {
          return typeof item === "number" ? (
            <PaginationItem
              key={item}
              page={item}
              active={page === item}
              onClick={handlePageChange}
            />
          ) : (
            <PaginationItem key={item} />
          );
        })}
      </ul>
      <Button
        data-testid="Pagination__next"
        variant="icon"
        classes={{ root: buttonClasses }}
        onClick={handleNextPage}
        style={{ visibility: showNextNavigation }}
      >
        <NavArrowNextIcon />
      </Button>
    </div>
  );
};

export default Pagination;
